import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { EntityStatus } from "../types";
import { UNCATEGORIZED_CATEGORY_ID } from "../utils/explorerUtils";

const DEBOUNCE_MS = 300;
const ALL_STATUSES = Object.values(EntityStatus);

export type ExplorerLayoutMode = "card" | "list";
export type ExplorerSortDirection = "asc" | "desc";

export interface UseExplorerUrlStateOptions {
  defaultSort: string;
  defaultLayout?: ExplorerLayoutMode;
}

export interface ExplorerUrlState {
  q: string;
  setQ: (value: string) => void;
  debouncedQ: string;
  sort: string;
  setSort: (value: string) => void;
  sortDir: ExplorerSortDirection;
  setSortDir: (value: ExplorerSortDirection) => void;
  layout: ExplorerLayoutMode;
  setLayout: (value: ExplorerLayoutMode) => void;
  page: number;
  setPage: (page: number) => void;
  statuses: EntityStatus[];
  toggleStatus: (status: EntityStatus) => void;
  categoryIds: string[];
  toggleCategoryId: (categoryId: string) => void;
  clearCategoryIds: () => void;
  tags: string[];
  toggleTag: (tag: string) => void;
  clearTags: () => void;
  clearAll: () => void;
}

function parseStatuses(raw: string | null): EntityStatus[] {
  if (!raw) return [];
  return raw
    .split(",")
    .map((s) => s.trim())
    .filter((s): s is EntityStatus =>
      ALL_STATUSES.includes(s as EntityStatus),
    );
}

function parseCategoryIds(raw: string | null): string[] {
  if (!raw) return [];
  return raw
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
}

function parseTags(raw: string | null): string[] {
  if (!raw) return [];
  return raw
    .split(",")
    .map((s) => {
      try {
        return decodeURIComponent(s.trim());
      } catch {
        return s.trim();
      }
    })
    .filter(Boolean);
}

function encodeTagsForUrl(tags: string[]): string {
  return tags.map((t) => encodeURIComponent(t)).join(",");
}

export function useExplorerUrlState(
  options: UseExplorerUrlStateOptions,
): ExplorerUrlState {
  const { defaultSort, defaultLayout = "card" } = options;
  const [searchParams, setSearchParams] = useSearchParams();

  const qFromUrl = searchParams.get("q") ?? "";
  const [qInput, setQInput] = useState(qFromUrl);
  const [debouncedQ, setDebouncedQ] = useState(qFromUrl);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    setQInput(qFromUrl);
    setDebouncedQ(qFromUrl);
  }, [qFromUrl]);

  const sort = searchParams.get("sort") ?? defaultSort;
  const sortDir =
    (searchParams.get("dir") as ExplorerSortDirection) === "desc"
      ? "desc"
      : "asc";
  const layoutParam = searchParams.get("layout") as ExplorerLayoutMode | null;
  const layout: ExplorerLayoutMode =
    layoutParam === "list"
      ? "list"
      : layoutParam === "card"
        ? "card"
        : defaultLayout;
  const page = Math.max(1, parseInt(searchParams.get("page") ?? "1", 10) || 1);
  const statuses = parseStatuses(searchParams.get("status"));
  const categoryIds = parseCategoryIds(searchParams.get("categories"));
  const tags = parseTags(searchParams.get("tags"));

  const updateParams = useCallback(
    (mutate: (params: URLSearchParams) => void) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          mutate(next);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const setQ = useCallback(
    (value: string) => {
      setQInput(value);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        updateParams((params) => {
          if (value.trim()) params.set("q", value.trim());
          else params.delete("q");
          params.set("page", "1");
        });
        setDebouncedQ(value.trim());
      }, DEBOUNCE_MS);
    },
    [updateParams],
  );

  const setSort = useCallback(
    (value: string) => {
      updateParams((params) => {
        params.set("sort", value);
        params.set("page", "1");
      });
    },
    [updateParams],
  );

  const setSortDir = useCallback(
    (value: ExplorerSortDirection) => {
      updateParams((params) => {
        if (value === "asc") params.delete("dir");
        else params.set("dir", "desc");
        params.set("page", "1");
      });
    },
    [updateParams],
  );

  const setLayout = useCallback(
    (value: ExplorerLayoutMode) => {
      updateParams((params) => {
        if (value === "card") params.delete("layout");
        else params.set("layout", value);
      });
    },
    [updateParams],
  );

  const setPage = useCallback(
    (p: number) => {
      updateParams((params) => {
        if (p <= 1) params.delete("page");
        else params.set("page", String(p));
      });
    },
    [updateParams],
  );

  const toggleStatus = useCallback(
    (status: EntityStatus) => {
      updateParams((params) => {
        const current = parseStatuses(params.get("status"));
        const next = current.includes(status)
          ? current.filter((s) => s !== status)
          : [...current, status];
        if (next.length) params.set("status", next.join(","));
        else params.delete("status");
        params.set("page", "1");
      });
    },
    [updateParams],
  );

  const toggleCategoryId = useCallback(
    (categoryId: string) => {
      updateParams((params) => {
        const current = parseCategoryIds(params.get("categories"));
        const next = current.includes(categoryId)
          ? current.filter((id) => id !== categoryId)
          : [...current, categoryId];
        if (next.length) params.set("categories", next.join(","));
        else params.delete("categories");
        params.set("page", "1");
      });
    },
    [updateParams],
  );

  const clearCategoryIds = useCallback(() => {
    updateParams((params) => {
      params.delete("categories");
      params.set("page", "1");
    });
  }, [updateParams]);

  const toggleTag = useCallback(
    (tag: string) => {
      updateParams((params) => {
        const current = parseTags(params.get("tags"));
        const next = current.includes(tag)
          ? current.filter((t) => t !== tag)
          : [...current, tag];
        if (next.length) params.set("tags", encodeTagsForUrl(next));
        else params.delete("tags");
        params.set("page", "1");
      });
    },
    [updateParams],
  );

  const clearTags = useCallback(() => {
    updateParams((params) => {
      params.delete("tags");
      params.set("page", "1");
    });
  }, [updateParams]);

  const clearAll = useCallback(() => {
    setSearchParams({}, { replace: true });
    setQInput("");
    setDebouncedQ("");
  }, [setSearchParams]);

  return {
    q: qInput,
    setQ,
    debouncedQ,
    sort,
    setSort,
    sortDir,
    setSortDir,
    layout,
    setLayout,
    page,
    setPage,
    statuses,
    toggleStatus,
    categoryIds,
    toggleCategoryId,
    clearCategoryIds,
    tags,
    toggleTag,
    clearTags,
    clearAll,
  };
}

export { UNCATEGORIZED_CATEGORY_ID };
